import React, { useEffect, useState } from "react";
import moment from "moment-timezone";
import styles from "./App.css";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("login"); // 'login' or 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const API_BASE_URL = "https://api.mxgold.net";
  const userTimezone = moment.tz.guess();
  const fetchConfig = { headers: { "x-user-timezone": userTimezone } };

  useEffect(() => {
    const getUser = async () => {
      try {
        setLoading(true);
        const localData = localStorage.getItem("user");

        if (localData) {
          const parsedData = JSON.parse(localData);
          setUser(parsedData);

          if (parsedData.uuid) {
            const response = await fetch(
              `${API_BASE_URL}/user/${parsedData.uuid}`,
              fetchConfig
            );
            if (!response.ok) throw new Error("Failed to fetch data from API");

            const data = await response.json();
            if (data.uuid) {
              localStorage.setItem("user", JSON.stringify(data));
              setUser(data);
            }
          }
        } else {
          const urlParams = new URLSearchParams(window.location.search);
          const referrerId = urlParams.get("referrer_id");
          const userUrl = referrerId
            ? `${API_BASE_URL}/user?referrer_id=${referrerId}`
            : `${API_BASE_URL}/user`;

          const response = await fetch(userUrl, fetchConfig);
          if (!response.ok) throw new Error("Failed to fetch data from API");

          const data = await response.json();
          if (data.uuid) {
            localStorage.setItem("user", JSON.stringify(data));
            setUser(data);
          }
        }
      } catch (error) {
        console.error("Error in getUser:", error);
      } finally {
        setLoading(false);
      }
    };

    getUser();
  }, []);

  const openModal = (type) => {
    setModalType(type);
    setShowModal(true);
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setError("");
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (modalType === "register" && password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const endpoint = modalType === "login" ? "auth/login" : "auth/register";
    const url = `${API_BASE_URL}/${endpoint}`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, uuid: user.uuid }),
      });

      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to submit form");

      setUser(data.user);
      closeModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const logout = async (e) => {
    e.preventDefault();

    const url = `${API_BASE_URL}/auth/logout`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uuid: user.uuid }),
      });

      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to submit form");

      setUser(data.user);
    } catch (err) {
      console.log(err.message);
    }
  };

  const handleCollectClick = async () => {
    try {
      setCollecting(true);
      const localData = localStorage.getItem("user");

      if (localData) {
        const parsedData = JSON.parse(localData);

        if (parsedData.uuid) {
          const response = await fetch(
            `${API_BASE_URL}/collect/${parsedData.uuid}`,
            fetchConfig
          );
          if (!response.ok) throw new Error("Failed to fetch data from API");

          const data = await response.json();
          if (data.user.uuid) {
            localStorage.setItem("user", JSON.stringify(data.user));
            setUser(data.user);
            alert(`New balance: ${data.user.total_balance}`);
          }
        }
      }
    } catch (error) {
      console.error("Error in callCollect:", error);
    } finally {
      setCollecting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header class="header">
        <div class="leftSection">
          <div class="logo">
            <span class="logoText">MXGOLD</span>
          </div>
        </div>
        <div class="navButtons">
          {user.session_id ? (
            <button onClick={(e) => logout(e)} className="button-outline">
              Log out
            </button>
          ) : (
            <>
              <button
                onClick={() => openModal("login")}
                className="button-outline"
              >
                Log in
              </button>
              <button
                onClick={() => openModal("register")}
                className="button-primary"
              >
                Register
              </button>
            </>
          )}
        </div>
      </header>

      {showModal && (
        <dialog open className="modal">
          <article>
            <header>
              <h2>{modalType === "login" ? "Log in" : "Register"}</h2>
              <button className="close" onClick={closeModal}>
                {/* &times; */}
              </button>
            </header>
            <form onSubmit={handleSubmit}>
              <label>
                Email:
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
              <label>
                Password:
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>
              {modalType === "register" && (
                <label>
                  Retype Password:
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </label>
              )}
              {error && <p className="error">{error}</p>}
              <button type="submit">
                {modalType === "login" ? "Log In" : "Register"}
              </button>
            </form>
          </article>
        </dialog>
      )}

      <div className={styles.card}>
        <h1 className={styles.title}>MXGOLD Collector</h1>
        <div className={styles.content}>
          {user && (
            <div className={styles.userInfo}>
              <p>UUID: {user.uuid}</p>
              <p>Balance: {user.total_balance} MXGOLD</p>
              <p>Timezone: {userTimezone}</p>
              <p>
                Local Time:{" "}
                {moment().tz(userTimezone).format("YYYY-MM-DD HH:mm:ss z")}
              </p>
            </div>
          )}
          <button
            onClick={handleCollectClick}
            disabled={collecting}
            className={styles.button}
          >
            {collecting ? (
              <span>
                <div className={styles.buttonSpinner}></div>
                Collecting...
              </span>
            ) : (
              "Collect 100 MXGOLD"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
